import React, { SyntheticEvent, useRef } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Body, SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { withLocalePrefix } from 'shared/utils/translation';
import { IconButton } from '@grnhse/seedling/lib/birch/components/buttons';
import CloseIcon from '@grnhse/seedling/lib/birch/icons/close';
import StyledTag from 'talent_matching/components/shared/styled_tag';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import capitalize from 'lodash/capitalize';
import Popover from '@mui/material/Popover';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import ArrowDownIcon from '@grnhse/seedling/lib/birch/icons/arrow_down';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';

const t = withLocalePrefix('talent_matching');

type Skill = {
  name: string;
  importance: number;
  exact?: boolean;
  base_skill?: string;
};

type Props = {
  skills: Skill[];
  openCalibrationModal: () => void;
};

const MatchingSkills = ({ skills, openCalibrationModal }: Props) => {
  const [popperIsOpen, openPopper, closePopper] = useModal();
  const anchorEl = useRef<HTMLDivElement | null>(null);

  const matchingSkills = skills.sort((a, b) => b.importance - a.importance);

  const onClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    openPopper();
  };

  if (matchingSkills.length === 0) {
    return null;
  }

  const splitExactAndSimilarSkills = matchingSkills.some(
    (skill) => skill.exact !== undefined
  );

  let similarSkills: Skill[] = [];
  let exactSkills: Skill[] = matchingSkills;
  if (splitExactAndSimilarSkills) {
    similarSkills = matchingSkills.filter((skill) => skill.exact === false);
    exactSkills = matchingSkills.filter((skill) => skill.exact === true);
  }

  const onOpenCalibrationModal = () => {
    closePopper();
    openCalibrationModal();
  };

  return (
    <Stack ref={anchorEl}>
      <Grid>
        <TertiaryButton
          onClick={onClick}
          size="md"
          icon={ArrowDownIcon}
          placement="right"
        >
          {t('skills_matched', { count: matchingSkills.length })}
        </TertiaryButton>
      </Grid>

      <Popover
        open={popperIsOpen}
        onClose={closePopper}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Stack
          sx={{
            padding: `${Tokens.spacing['2']} ${Tokens.spacing['3']}`,
            maxWidth: '300px',
          }}
        >
          <Stack sx={{ padding: 0, minWidth: '200px' }} spacing={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Body medium>{t('calibrated_skills')}</Body>
              <IconButton
                icon={CloseIcon}
                onClick={closePopper}
                label={t('close')}
                flat
              />
            </Stack>
            {splitExactAndSimilarSkills && exactSkills.length > 0 && (
              <SecondaryBody>{t('exact_keywords')}</SecondaryBody>
            )}
            <Stack
              spacing={0.5}
              direction="row"
              flexWrap="wrap"
              alignContent="flex-start"
              sx={{
                padding: 0,
                paddingBottom: 1,
                height: '100%',
              }}
            >
              {exactSkills.map((skill) => (
                <StyledTag
                  key={skill.name}
                  bgColor={FullPalette.GREY_E500}
                  text={skill.base_skill || capitalize(skill.name)}
                />
              ))}
            </Stack>
            {splitExactAndSimilarSkills && similarSkills.length > 0 && (
              <>
                <SecondaryBody>{t('similar_keywords')}</SecondaryBody>
                <Stack
                  spacing={0.5}
                  direction="row"
                  flexWrap="wrap"
                  alignContent="flex-start"
                  sx={{ padding: 0, paddingBottom: 1, height: '100%' }}
                >
                  {similarSkills.map((skill) => (
                    <StyledTag
                      key={skill.name}
                      bgColor={FullPalette.GREY_E500}
                      text={capitalize(skill.name)}
                    />
                  ))}
                </Stack>
              </>
            )}
            <Stack direction="row" justifyContent="flex-start">
              <TertiaryButton onClick={onOpenCalibrationModal} size="md">
                {t('edit_calibration')}
              </TertiaryButton>
            </Stack>
          </Stack>
        </Stack>
      </Popover>
    </Stack>
  );
};

export default MatchingSkills;
